import React from "react";
import { BadgeCheck, Bookmark } from "lucide-react";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { useSavedAlerts } from "@/hooks/useSavedAlerts";

/** Small pill badge from the redesign (8 px on desktop rows, 10 px on mobile cards). */
export const Pill: React.FC<{
  tone: "brand" | "neutral" | "danger";
  children: React.ReactNode;
  className?: string;
}> = ({ tone, children, className = "" }) => {
  const tones = {
    brand: "border-[#00653b] bg-[#002c14] text-[#56d799]",
    neutral: "border-[#404040] bg-[#171717] text-[#d4d4d4]",
    danger: "border-[#7f1d1d] bg-[#2a0a0a] text-[#f87171]",
  };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 font-medium ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
};

/** "NEW" for alerts under a day old, then the token's CoinMarketCap tags. */
export const AlertTagBadges: React.FC<{
  isNew: boolean;
  isListing: boolean;
  tags: string[];
  textClass?: string;
  gapClass?: string;
}> = ({ isNew, isListing, tags, textClass = "text-[8px] leading-3", gapClass = "gap-0.5" }) => {
  if (!isNew && tags.length === 0 && isListing) return null;
  return (
    <div className={`flex flex-wrap ${gapClass}`}>
      {!isListing && (
        <Pill tone="danger" className={textClass}>
          DELISTING
        </Pill>
      )}
      {isNew && (
        <Pill tone="brand" className={textClass}>
          NEW
        </Pill>
      )}
      {tags.slice(0, isNew ? 1 : 2).map((tag) => (
        <Pill key={tag} tone="neutral" className={`max-w-[120px] truncate ${textClass}`}>
          {tag}
        </Pill>
      ))}
    </div>
  );
};

/** Token is known to CoinMarketCap (we have its metadata). */
export const VerifiedMark: React.FC = () => (
  <BadgeCheck className="size-3 shrink-0 text-[#22c55e]" aria-label="Known token" />
);

/** Overlapping logos of the venues that listed the token at alert time, then "+N". */
export const ExchangeStack: React.FC<{
  codes: string[];
  max?: number;
  size?: number;
}> = ({ codes, max = 2, size = 24 }) => {
  if (codes.length === 0) return null;
  const shown = codes.slice(0, max);
  const more = codes.length - shown.length;
  return (
    <div className="flex items-center">
      {shown.map((code, i) => (
        <ExchangeLogo
          key={code}
          code={code}
          size={size}
          className={`border-[1.5px] border-[#0d121a] ${i > 0 ? "-ml-1" : ""}`}
        />
      ))}
      {more > 0 && (
        <span
          style={{ width: size, height: size }}
          className="-ml-1 flex shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#0d121a] bg-[#262626] text-xs text-[#a3a3a3]"
        >
          +{more}
        </span>
      )}
    </div>
  );
};

/** Bookmark toggle (saved on this device). */
export const BookmarkButton: React.FC<{ alertId: string; className?: string }> = ({
  alertId,
  className = "",
}) => {
  const { isSaved, toggle } = useSavedAlerts();
  const saved = isSaved(alertId);
  return (
    <button
      type="button"
      aria-label={saved ? "Remove bookmark" : "Bookmark alert"}
      aria-pressed={saved}
      onClick={(e) => {
        e.stopPropagation();
        toggle(alertId);
      }}
      className={`flex items-center justify-center rounded-full border border-white/10 bg-[rgba(13,18,26,0.7)] p-2 transition-colors hover:border-white/25 ${className}`}
    >
      <Bookmark
        className={`size-3 ${saved ? "fill-[#00c27e] text-[#00c27e]" : "text-[#737373]"}`}
      />
    </button>
  );
};
